import { z } from "zod";
import { CREDENTIAL_TARGET_TYPES } from "./credentialTargets";
import type { DbtCauseView } from "./dbtCauses";
import { TEXT_CAUSE_CODES } from "./importCauses";
import { RUN_STATUSES, STEP_NAMES, type RunSummaryRow } from "./runs";
import type { DbtJobRunRow } from "./transformations";

/**
 * `GET /api/v2/overview` (Session 10, P1): one read that answers the Overview screen.
 *
 * Every section is computed only when the caller's role in this instance holds the section's
 * read permission; otherwise it is `null` and `unavailable[section]` says `"forbidden"`. A
 * section whose read threw is `null` with `"error"`, and the other sections still answer. A
 * count that could not be read is therefore never a zero: a client reads `null` plus the reason.
 *
 * SEC-17: counts, identifiers, names, enums and the runner's own step sentence (cut to 200
 * characters). No binding field other than the three `destination` facts leaves the service, and
 * the section never goes through the audited binding read (`binding.read`), so polling this
 * route writes no audit row.
 */

export const OVERVIEW_SECTIONS = [
  "destination",
  "connections",
  "schedules",
  "runs",
  "imports",
  "tests",
  "alerts",
  // 2026-09-28 cred-check: the Credentials card, and the runner's last heartbeat (owner answer f).
  "credentials",
  "runner",
] as const;
export type OverviewSection = (typeof OVERVIEW_SECTIONS)[number];

export const OVERVIEW_UNAVAILABLE_REASONS = ["forbidden", "error"] as const;
export type OverviewUnavailableReason = (typeof OVERVIEW_UNAVAILABLE_REASONS)[number];

/** The rolling window of the imports card, labelled "last 24 hours" (decision Q3). */
export const OVERVIEW_IMPORT_WINDOW_HOURS = 24;
/** The most runs the imports card sums; `capped` says the window held more. */
export const OVERVIEW_IMPORT_RUN_CAP = 500;
export const OVERVIEW_FAILING_SAMPLE_MAX = 3;
export const OVERVIEW_NEXT_FIRES_MAX = 5;
export const OVERVIEW_RECENT_RUNS = 5;
/** At most three run alerts plus the latest transform's (review r1: max 4, not 5). */
export const OVERVIEW_RUN_ALERTS_MAX = 3;
export const OVERVIEW_ALERTS_MAX = 4;
/** The runner's step sentence is cut to this length in an alert's `detail`. */
export const OVERVIEW_ALERT_DETAIL_MAX = 200;

const count = z.number().int().min(0);
const iso = z.string();

export const runStatusCountsSchema = z.object({
  queued: count,
  running: count,
  succeeded: count,
  partially_succeeded: count,
  failed: count,
  cancelled: count,
  total: count,
});
export type RunStatusCounts = z.infer<typeof runStatusCountsSchema>;

const objectLike = (value: unknown): boolean => typeof value === "object" && value !== null;

export const overviewDestinationSchema = z
  .object({
    bound: z.boolean(),
    type: z.enum(["snowflake", "databricks"]).nullable(),
    transformSourceConfigured: z.boolean(),
  })
  .strict();

export const overviewConnectionsSchema = z.object({
  total: count,
  ready: count,
  draft: count,
  disabled: count,
  /**
   * `dataflows.is_failed` on a connection that is not disabled (a disabled one never counts), less
   * the ones that are `waiting`.
   */
  failing: count,
  /**
   * #787: failed connections whose newest failed run (#460: by creation, as the run list orders
   * runs, never a run queued after it) was refused only by readiness gates (`WAITING_CAUSES`): they
   * wait for setup to finish. Never counted in `failing`, and never read as healthy either.
   */
  waiting: count,
  /**
   * Connections a queued or running run holds now, read from the run rows as the Connections
   * list's Running is (`activeRunsByDataflow`, #2094 review), never the `is_processing` lock,
   * which finalize clears after the run row. A disabled connection never counts.
   */
  processing: count,
  /**
   * At most three failing connections (never a disabled one), the most recent failure first: by
   * when the newest failed run finished (or, with none on record, the connection's last run).
   */
  failingSample: z
    .array(
      z.object({
        id: z.string(),
        /** The connection's Name (`dataflows.name`, instance 0015): the table-name identifier. */
        name: z.string(),
        /** The connection's Display name: the one a person reads. */
        displayName: z.string(),
        /**
         * #460: the connection's newest failed run, the run the failure link opens. Not
         * `dataflows.last_run_id`, which can name a run queued after the failure. Null when no
         * failed run of the connection is on record (removed by retention, or never written).
         */
        lastRunId: z.string().nullable(),
      }),
    )
    .max(OVERVIEW_FAILING_SAMPLE_MAX),
});

export const overviewSchedulesSchema = z.object({
  total: count,
  enabled: count,
  /** Enabled schedules with a next fire, soonest first, each in its own zone. */
  nextFires: z
    .array(
      z.object({
        scheduleId: z.string(),
        name: z.string(),
        nextFireAt: iso,
        timezone: z.string(),
      }),
    )
    .max(OVERVIEW_NEXT_FIRES_MAX),
});

/** How many rolling 24-hour windows the Runs card's strip draws (round 4). */
export const OVERVIEW_RUN_DAYS = 7;

export const overviewRunsSchema = z.object({
  last24h: runStatusCountsSchema,
  last7d: runStatusCountsSchema,
  /**
   * Round 4 (2026-09-30): the Runs card's 7-day strip. One entry per rolling 24-hour window
   * ending now, OLDEST first: entry 6 is `last24h`, entry 5 the 24 hours before it, and together
   * they are `last7d`. Rolling windows, not calendar days, so the answer needs no time zone.
   * Optional so an older server's answer still parses (the card then draws no strip).
   */
  daily: z.array(runStatusCountsSchema).length(OVERVIEW_RUN_DAYS).optional(),
  /** `RunService.list({ limit: 5 })`: the same rows the Runs screen shows. */
  recent: z.array(z.custom<RunSummaryRow>(objectLike)).max(OVERVIEW_RECENT_RUNS),
  /**
   * Round 10 (O5): the runs in flight right now, queued and running, with no time window. The
   * "Running now" tile reads it. Optional so an older server's answer still parses (no tile).
   */
  active: z.object({ running: count, queued: count }).optional(),
});

/** #1240: some rows of the imports gap and how many runs gave them. */
const rowsPartSchema = z.object({ rows: count, runs: count }).strict();

export const overviewImportsSchema = z.object({
  windowHours: z.literal(OVERVIEW_IMPORT_WINDOW_HOURS),
  /** Sum over the counted runs that have totals; null when none of them has any. */
  rowsExtracted: count.nullable(),
  /** Sum of the loaded counts that were reported; null when no counted run reported one. */
  rowsLoaded: count.nullable(),
  /** #1375: how many different tables (connection and table) the counted runs imported, never loads. */
  tables: count,
  /** #1375: how many of those tables failed in their newest load in the counted runs. */
  tablesFailed: count,
  /** Runs created in the window that were examined (at most `OVERVIEW_IMPORT_RUN_CAP`). */
  runsCounted: count,
  /** Counted runs with no tables at all: transform-only runs, and runs that have not extracted yet. */
  runsWithoutTotals: count,
  /** True when the window held more runs than `OVERVIEW_IMPORT_RUN_CAP`; the newest were counted. */
  capped: z.boolean(),
  /**
   * #1240: why Extracted is more than Loaded. For each counted run whose extract reported more
   * rows than its load (a load that reported nothing counts as none loaded, except on a
   * succeeded run, which is `loadNotReported`), that difference,
   * added up by the run's status, with how many runs gave it. A status with no such run is absent.
   * Optional so an older server's answer still parses (the card then gives no reason).
   */
  notLoaded: z.partialRecord(z.enum(RUN_STATUSES), rowsPartSchema).optional(),
  /**
   * #1240: the succeeded runs whose load reported no row figure (none of their extract is in
   * Loaded, but it is not "fewer rows"), with their extracted rows. Optional, like `notLoaded`.
   */
  loadNotReported: rowsPartSchema.optional(),
  /**
   * #1240: the runs whose load reported more rows than their extract counted, by how many. With
   * it, `notLoaded` + `loadNotReported` - `loadedMore` = `rowsExtracted - (rowsLoaded ?? 0)`.
   */
  loadedMore: rowsPartSchema.optional(),
});
export type OverviewNotLoaded = NonNullable<z.infer<typeof overviewImportsSchema>["notLoaded"]>;
export type OverviewRowsPart = z.infer<typeof rowsPartSchema>;

export const overviewTestsSchema = z.object({
  /** `DbtJobRunsService.listRuns(1)`: the latest schedule-level transform step. */
  latest: z.custom<DbtJobRunRow>(objectLike).nullable(),
});

export const OVERVIEW_ALERT_KINDS = [
  "run_failed",
  "run_partial",
  "transform_failed",
  "tests_failed",
] as const;
export type OverviewAlertKind = (typeof OVERVIEW_ALERT_KINDS)[number];

export const overviewAlertSchema = z.object({
  kind: z.enum(OVERVIEW_ALERT_KINDS),
  runId: z.string(),
  /**
   * For a run alert, when the run was created; for a transform alert, when its transform step
   * was created (`DbtJobRunRow.createdAt`).
   */
  at: iso,
  /** A sentence, for example "Import from Azure SQL test source failed at load". */
  title: z.string(),
  /** The first failed step's `error` (the runner's sentence), cut to 200 characters. */
  detail: z.string().max(OVERVIEW_ALERT_DETAIL_MAX).nullable(),
  connectionName: z.string().nullable(),
  step: z.enum(STEP_NAMES).nullable(),
  cause: z.custom<DbtCauseView>(objectLike).nullable(),
});
export type OverviewAlert = z.infer<typeof overviewAlertSchema>;

/** The Credentials card names at most this many targets that need attention. */
export const OVERVIEW_CREDENTIALS_MAX = 5;

/** #1306: the destination's credentials as a reader below Instance admin learns of them. */
export const overviewDestinationWithheldSchema = z
  .object({
    present: z.boolean(),
    needsAttention: z.boolean(),
    /**
     * How many destination targets (stage, warehouse, dbt repository) need attention, so the
     * heading counts what an admin's counts. A number only, never which one.
     */
    count: count,
    kind: z.enum(["refused", "expired", "expiring"]).nullable(),
  })
  .strict();

export const overviewCredentialsSchema = z.object({
  /** False when `features.flags["credentials.check"]` is false: the card says checks are off. */
  checksEnabled: z.boolean(),
  /** Connections and destination sections with a check row or an expiry date. */
  targets: count,
  /** Targets refused, expired, or expiring within 30 days. */
  needAttention: count,
  /** The most urgent first: refused, then expired, then the nearest date. */
  attention: z
    .array(
      z.object({
        targetType: z.enum(CREDENTIAL_TARGET_TYPES),
        targetId: z.string(),
        name: z.string(),
        kind: z.enum(["refused", "expired", "expiring"]),
        daysLeft: z.number().int().nullable(),
        /** #1097: what the badge's words read, as on the connection (`credentialAttentionLabel`). */
        connectorName: z.string().nullable(),
        causeCode: z.enum(TEXT_CAUSE_CODES).nullable(),
      }),
    )
    .max(OVERVIEW_CREDENTIALS_MAX),
  /** The newest check of any target, or null before the first. */
  lastCheckedAt: iso.nullable(),
  /**
   * #1306: present only for a reader without `instance.binding.read` (below Instance admin). The
   * destination's rows are then left out of `targets`, `needAttention` and `attention`, and this
   * says only whether one is on the list, whether one needs attention and the most urgent closed
   * kind and how many, so the card can count them, colour it as for an admin and say who to ask. Never a name, a
   * cause or a date. `lastCheckedAt` still counts the destination's checks.
   */
  destinationWithheld: overviewDestinationWithheldSchema.optional(),
});

/** The runner's newest heartbeat (owner answer f); `stale` past ten minutes or with none. */
export const overviewRunnerSchema = z.object({
  lastSeenAt: iso.nullable(),
  stale: z.boolean(),
});

export const overviewSchema = z.object({
  generatedAt: iso,
  destination: overviewDestinationSchema.nullable(),
  connections: overviewConnectionsSchema.nullable(),
  schedules: overviewSchedulesSchema.nullable(),
  runs: overviewRunsSchema.nullable(),
  imports: overviewImportsSchema.nullable(),
  tests: overviewTestsSchema.nullable(),
  alerts: z.array(overviewAlertSchema).max(OVERVIEW_ALERTS_MAX).nullable(),
  credentials: overviewCredentialsSchema.nullable(),
  runner: overviewRunnerSchema.nullable(),
  unavailable: z.partialRecord(z.enum(OVERVIEW_SECTIONS), z.enum(OVERVIEW_UNAVAILABLE_REASONS)),
});

export type OverviewDestination = z.infer<typeof overviewDestinationSchema>;
export type OverviewConnections = z.infer<typeof overviewConnectionsSchema>;
export type OverviewDestinationWithheld = z.infer<typeof overviewDestinationWithheldSchema>;
export type OverviewSchedules = z.infer<typeof overviewSchedulesSchema>;
export type OverviewRuns = z.infer<typeof overviewRunsSchema>;
export type OverviewImports = z.infer<typeof overviewImportsSchema>;
export type OverviewTests = z.infer<typeof overviewTestsSchema>;
export type OverviewCredentials = z.infer<typeof overviewCredentialsSchema>;
export type OverviewRunner = z.infer<typeof overviewRunnerSchema>;
export type Overview = z.infer<typeof overviewSchema>;

/** Zeroed counts for every run status, in `RUN_STATUSES` order, plus `total`. */
export function emptyRunStatusCounts(): RunStatusCounts {
  const counts = { total: 0 } as RunStatusCounts;
  for (const status of RUN_STATUSES) counts[status] = 0;
  return counts;
}
